/* eslint-disable */
import moment from 'moment';
import {nanoid} from 'nanoid';

// Forms time intervals for time periods
export function intervals(startString = '', endString = '', interval = 15) {
  const result = [];
  const start = moment(startString, 'h:mm A');
  const end = moment(endString, 'h:mm A');

  start.minutes(Math.ceil(start.minutes() / interval) * interval);

  const current = moment(start);

  while (current <= end) {
    result.push(current.format('h:mm A'));
    current.add(interval, 'minutes');
  }

  return result;
}

export function sortPeriods(first, next) {
  if (first.Start < next.Start) {
    return -1;
  }

  if (first.Start > next.Start) {
    return 1;
  }

  return 0;
}

// Parser that forms data objects to send to the server
const getMinutesFromWeekStart = (d, time) =>
  moment(new Date(0)).day(d).weekday() * 24 * 60 +
  time.get('hour') * 60 +
  time.get('minute');

export const openingDaysServerParser = (days) => {
  const result = [];
  days?.forEach((day) => {
    day.days.map((d) => {
      const resultObjects = day.timePeriods
        .filter(
          (period) =>
            period.endOfWorkingDay.trim() !== 'AM' &&
            period.startOfWorkingDay.trim() !== 'AM' &&
            period.endOfWorkingDay.trim() !== 'PM' &&
            period.startOfWorkingDay.trim() !== 'PM'
        )
        .map((period) => {
          const start = moment(period.startOfWorkingDay, 'h:mm A');
          let end = moment(period.endOfWorkingDay, 'h:mm A');
          const startPeriod = getMinutesFromWeekStart(d, start);
          if (end.isSameOrBefore(start)) {
            end.add(1, 'day');
          }

          const durationPeriod =
            moment.duration(moment(end).diff(start)).asHours() * 60;
          return {Start: startPeriod, Duration: Math.round(durationPeriod)};
        });

      const nextArray = resultObjects.sort((a, b) => {
        if (a.Start > b.Start) {
          return 1;
        }

        if (a.Start < b.Start) {
          return -1;
        }

        return 0;
      });

      let candidate = resultObjects[0];
      const mergedResultObjects = [candidate];
      for (let i = 1; i < resultObjects.length; i++) {
        const value = resultObjects[i];
        const valueEnd = value.Start + value.Duration;
        const candidateEnd = candidate.Start + candidate.Duration;

        if (value.Start <= candidateEnd && valueEnd >= candidateEnd) {
          candidate.Duration = valueEnd - candidate.Start;
        }

        if (value.Start > candidateEnd) {
          mergedResultObjects.push(value);
          candidate = mergedResultObjects[mergedResultObjects.length - 1];
        }
      }
      result.push(resultObjects);
    });
  });
  const sortDays = result.flat().sort(sortPeriods);
  return mergeOpeningHours(
    sortDays.reduce((acc, current) => {
      const x = acc.find(
        (item) =>
          item.Start === current.Start && item.Duration === current.Duration
      );
      if (!x) {
        return acc.concat([current]);
      } else {
        return acc;
      }
    }, [])
  );
};

//Merges data objects before sending to the server
export function mergeOpeningHours(list) {
  const nextList = [];
  const sortList = list.sort(sortPeriods);
  const MAX_END_SLOT_TIME = 10080;
  const FULL_DAY_TIME = 1440;

  for (const period of sortList) {
    const prevTime = nextList.length ? nextList[nextList.length - 1] : null;

    // Check if previous period start later than current period or intersects with current period
    if (
      prevTime &&
      (prevTime.Start > period.Start ||
        prevTime.Start + prevTime.Duration > period.Start) &&
      prevTime.Start + prevTime.Duration < period.Start + period.Duration
    ) {
      prevTime.Duration = period.Start - prevTime.Start;
    }
    if (
      (prevTime &&
        period.Start !== prevTime.Start + prevTime.Duration &&
        ((prevTime.Start > period.Start &&
          prevTime.Start + prevTime.Duration >
            period.Start + period.Duration) ||
          (prevTime.Start < period.Start &&
            prevTime.Start + prevTime.Duration <
              period.Start + period.Duration))) ||
      !prevTime
    ) {
      nextList.push(period);
    } else if (
      prevTime &&
      period.Start === prevTime.Start + prevTime.Duration
    ) {
      if (prevTime.Start + prevTime.Duration < period.Start + period.Duration) {
        if (period.Start + period.Duration - prevTime.Start > FULL_DAY_TIME) {
          if (period.Start === prevTime.Duration + prevTime.Start) {
            const day = moment(new Date(0))
              .startOf('week')
              .set('minutes', period.Start)
              .get('day');
            const newTime = getMinutesFromWeekStart(
              day,
              moment('12:00 AM', 'h:mm A')
            );
            prevTime.Duration = newTime - prevTime.Start;
            nextList.push({
              Start: newTime,
              Duration: period.Start - newTime + period.Duration
            });
          } else {
            nextList.push(period);
          }
        } else {
          prevTime.Duration = period.Start + period.Duration - prevTime.Start;
        }
      }
    }
  }

  const lastSlot = nextList.length > 1 && nextList[nextList.length - 1];
  const firstSlot = nextList.length > 1 && nextList[0];

  // Same logic as in the for loop, but it checks the intersection around the week, e.g. Saturday intersects with Sunday

  return nextList.filter((item) => item.Duration !== 0);
}

// generates time ranges for time selectors
export function generateTimeRanges(
  interval,
  language = window.navigator.language,
  from = 0,
  to = 24
) {
  const selectedRanges = {};
  const date = new Date();
  const format = {
    hour: 'numeric',
    minute: 'numeric'
  };

  for (
    let minutes = from * 60;
    minutes < (to === 24 ? 24 : to + 0.2) * 60;
    minutes += interval
  ) {
    date.setHours(0);
    date.setMinutes(minutes);
    selectedRanges[date.toLocaleTimeString(language, format)] = false;
  }
  return selectedRanges;
}

export const checkDuringHours = (value) => {
  return value?.filter((item) => item.Duration === 1440)?.length;
};

const padZero = (value) => {
  return value.toString().padStart(2, '0');
};

const formatTime = (hour, minute) => {
  const h = hour % 12 || 12;
  const ampm = hour < 12 ? 'AM' : 'PM';
  return `${h}:${padZero(minute)} ${ampm}`;
};

const getDuration = (start, end) => {
  let startMinutes = start.hour * 60 + start.minute;
  let endMinutes = end.hour * 60 + end.minute;
  let total = endMinutes - startMinutes;
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return `${hours}:${padZero(minutes)}`;
};

const generateHoursIntervals = (start, end) => {
  let intervals = [];
  let minutes = start.hour * 60 + start.minute;
  const endMinutes = end.hour * 60 + end.minute;
  while (minutes <= endMinutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    intervals.push(formatTime(h, m));
    minutes += 15;
  }
  return intervals;
};

export const convertScheduleFromServiceFormat = (input) => {
  return input.map((block) => {
    const timePeriods = block.time.map((timeBlock) => {
      const id = crypto.randomUUID();
      const startStr = formatTime(timeBlock.start.hour, timeBlock.start.minute);
      const endStr = formatTime(timeBlock.end.hour, timeBlock.end.minute);
      const duration = getDuration(timeBlock.start, timeBlock.end);
      const hoursIntervals = generateHoursIntervals(
        timeBlock.start,
        timeBlock.end
      );
      return {
        id,
        startOfWorkingDay: startStr,
        endOfWorkingDay: endStr,
        duration,
        hoursIntervals
      };
    });

    const flatDurations = timePeriods.map((tp) => tp.duration);
    const overallStart = timePeriods[0].startOfWorkingDay;
    const overallEnd = timePeriods[timePeriods.length - 1].endOfWorkingDay;

    return {
      id: nanoid(),
      days: block.days,
      startOfWorkingDay: overallStart,
      endOfWorkingDay: overallEnd,
      duration: flatDurations.length === 1 ? flatDurations[0] : null,
      timePeriods
    };
  });
};

const parseTimeString = (timeStr) => {
  const [time, meridian] = timeStr.split(' ');
  let [hour, minute] = time.split(':').map(Number);
  if (meridian === 'PM' && hour !== 12) hour += 12;
  if (meridian === 'AM' && hour === 12) hour = 0;
  return {hour, minute};
};

export const convertScheduleToServiceFormat = (input) => {
  return input.map((entry) => {
    const time = entry.timePeriods.map((tp) => ({
      start: parseTimeString(tp.startOfWorkingDay),
      end: parseTimeString(tp.endOfWorkingDay)
    }));

    return {
      days: entry.days,
      time
    };
  });
};
